import { Typography, TypographyProps } from "@cappelendamm/design-system/react";
import classNames from "classnames";
import Image from "next/image";
import Link, { LinkProps } from "next/link";
import { type JSX } from "react";

export type AutocompleteTemplateItemProps = {
  format?: string | null;
  imageSrc?: string | null;
  altText: string;
  highlightName: React.ReactNode;
  highlightDetails?: React.ReactNode;
  price?: string | null;
} & Pick<LinkProps, "href"> &
  JSX.IntrinsicElements["a"];

export type AutocompleteTemplateHeaderProps = TypographyProps &
  JSX.IntrinsicElements["p"];

export function AutocompleteTemplateHeader({
  children,
  className,
  ...rest
}: AutocompleteTemplateHeaderProps) {
  return (
    <Typography
      as="p"
      className={classNames(
        "mt-6 mb-4 border-b border-stroke-default pb-2 font-semibold",
        className,
      )}
      {...rest}
    >
      {children}
    </Typography>
  );
}

export function AutocompleteTemplateItem({
  imageSrc,
  format,
  altText,
  highlightName,
  highlightDetails,
  price,
  children,
  href,
  className,
  ...rest
}: AutocompleteTemplateItemProps) {
  return (
    <Link
      href={href}
      className={classNames(
        "relative mb-4 grid cursor-pointer grid-flow-col grid-cols-[auto_1fr_auto] gap-3 gap-x-8 px-4 py-2",
        className,
      )}
      tabIndex={0}
      role="button"
      {...rest}
    >
      {imageSrc && (
        <Image
          src={imageSrc}
          alt={altText}
          height={84}
          width={84}
          className="rounded-xl object-contain"
        />
      )}
      <div className="grid h-fit gap-y-3">
        <span className="h-5 truncate font-bold">{highlightName}</span>
        <span>{format}</span>
        {highlightDetails && <span>{highlightDetails}</span>}
        {price && <span>{price},–</span>}
      </div>
      {children}
    </Link>
  );
}
